// @flow
import * as React from 'react';
import moment from 'moment';
import { formatQuery } from 'url-lib';
import { t } from 'javascripts/utils/withTranslation';
import ErrorBoundary from 'javascripts/utils/ErrorBoundary';
import type { TeaserType } from 'components/organisms/teaser/react/Teaser';
import ListContainer from '../../list-container/react/ListContainer';
import List from '../../list/react/List';
import Icon from '../../../_particles/icon/react/Icon';

type Props = {
  url: string,
  categoryId: number,
  langId: number,
  countMedia: number,
  countPages: number,
  countPress: number,
  countPublications: number,
  extension: string,
};

type Offset = {
  media: number,
  pages: number,
  press: number,
  pub: number,
  [key: string]: number,
};

type State = {
  items: Array<TeaserType>,
  loading: boolean,
  error: boolean,
  offset: Offset,
  loadMore: boolean,
};

type Data = {
  table: string,
  dateAtom: string,
  title: string,
  subtitle: string,
  teasertext: string,
  link: string,
  image: {
    path: string,
    width: number,
    height: number,
    alt: string,
    title: string,
  },
  tags: Array<{ title: string, link: string }>,
  alt: string,
};

const Loader = () => (
  <div className="list-fetch__loader">
    <p className="list-fetch__loader-text">{t('Inhalte werden geladen.')}</p>
    <Icon icon="list-loader" className="list-fetch__loader-icon" />
  </div>
);

export default class ListFetch extends React.Component<Props, State> {
  limit = 10;

  eID = 'rsmbwthemenseiten_list';

  queryDefault: { eID: string, ... } = {
    eID: this.eID,
    [`${this.eID}[do]`]: 'list',
    [`${this.eID}[limit]`]: this.limit,
    [`${this.eID}[category]`]: this.props.categoryId,
    [`${this.eID}[lang]`]: this.props.langId,
    [`${this.eID}[extension]`]: this.props.extension,
  };

  state: State = {
    items: [],
    loading: true,
    error: false,
    offset: {
      media: this.props.countMedia,
      pages: this.props.countPages,
      press: this.props.countPress,
      pub: this.props.countPublications,
    },
    loadMore: true,
  };

  formatData = (
    data: Array<Data>
  ): { formatted: Array<TeaserType>, offset?: Offset } => {
    if (!data) {
      return { formatted: [] };
    }

    const formatted = [];
    const { offset } = this.state;

    data.map((item, i) => {
      const obj: TeaserType = {
        headline: item.title,
        moreLink: {
          link: item.link !== '' ? item.link : '#',
          text: t('Mehr'),
        },
        image: {
          src: item.image && item.image.path,
          alt: item.image && item.image.alt,
          width: item.image && item.image.width,
          height: item.image && item.image.height,
        },
        date: {
          date: item.dateAtom,
          text: moment(item.dateAtom).format('DD.MM.YYYY'),
        },
        hashtags: item.tags.map((tag) => ({ link: tag.link, text: tag.title })),
      };
      if (item.subtitle) {
        obj.meta = [{ text: item.subtitle }];
      }
      if (item.teasertext) {
        obj.text = item.teasertext;
      }
      // Add offset count
      if (item.table) {
        const indexer: $Keys<Offset> = item.table;
        offset[indexer] += 1;

        if (item.table === 'media') {
          obj.mediaType = { icon: 'play', text: 'Video' };

          if (!item.image || (item.image && !item.image.path)) {
            obj.image.src = undefined;
          }
        }

        if (item.table === 'pub') {
          obj.publication = true;
        }
      }

      // Add to formatted array
      formatted[i] = obj;

      return obj;
    });

    return {
      formatted,
      offset,
    };
  };

  fetchEntries = () => {
    const offset: any = {};
    Object.keys(this.state.offset).forEach((key) => {
      offset[`${this.eID}[offset${key}]`] = this.state.offset[key];
    });
    const searchQuery = {
      ...this.queryDefault,
      ...offset,
    };

    const url = `${this.props.url}?${formatQuery(searchQuery)}`;

    this.setState({
      items: [...this.state.items],
      loading: true,
    });

    fetch(url)
      .then((resp) => resp.json())
      .then((data) => {
        const formattedData = this.formatData(data);
        this.setState({
          items: [...this.state.items, ...formattedData.formatted],
          loading: false,
          offset: formattedData.offset,
          loadMore: formattedData.formatted.length >= this.limit,
        });
      })
      .catch((error) => {
        this.setState({
          items: [...this.state.items],
          loading: false,
          error,
        });
      });
  };

  componentDidMount() {
    this.fetchEntries();
  }

  render(): React.MixedElement {
    if (this.state.loading && this.state.items.length === 0) {
      return <Loader />;
    }

    if (this.state.items.length === 0 || this.state.error) {
      return (
        <p className="list-fetch__loader-text">
          {t('Es konnten keine weiteren Beiträge gefunden werden.')}
        </p>
      );
    }

    return (
      <ErrorBoundary>
        <ListContainer>
          <List
            items={this.state.items}
            loadMore={this.fetchEntries}
            loading={this.state.loading}
          />
        </ListContainer>
      </ErrorBoundary>
    );
  }
}
